import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import moment from 'moment';

import Button from '@components/custom/Button';
import Icon from '@components/custom/Icon';
import LatestWithdrawals from './components/LatestWithdrawals';
import ModuleHeader from '@components/ModuleHeader';
import ScrollList from '@components/custom/ScrollList';
import Select from '@components/custom/Select';
import Spinner from '@components/custom/Spinner';

import '@assets/css/finances.css';

import { crudActions } from '@sm/services/crudActions';
import { showAlert } from '@sm/store/alertSlice';

import { formatBalance } from '@sm/utils/utils';

const RequestWithdrawal = () => {
  const dispatch = useDispatch();

  const inputRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currencies, setCurrencies] = useState([]);
  const [selectedCurrency, setSelectedCurrency] = useState('');
  const [wallets, setWallets] = useState([]);
  const [selectedWallet, setSelectedWallet] = useState('');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawalFee, setWithdrawalFee] = useState(0);
  const [merchantId, setMerchantId] = useState(null);
  const [balance, setBalance] = useState(0);
  const [shouldRefreshList, setShouldRefreshList] = useState('');

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);

    crudActions.get('v1/clients/lookups/withdrawal')
      .then(response => {
        if (isMounted && response) {
          setCurrencies(response?.currencies);
          setSelectedCurrency(response?.currencies[0]?.value);
          setMerchantId(response?.merchantId);
        }
      })
      .catch(err => {
        if (isMounted) dispatch(showAlert('danger', err?.message));
      })
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [dispatch]);

  useEffect(() => {
    if (!selectedCurrency) return;
    let isMounted = true;
    setIsLoading(true);

    crudActions.get(`v1/wallets/balances?currency=${selectedCurrency}`)
      .then(response => {
        if (isMounted) {
          setBalance(parseFloat(response?.balance));
          setWallets(response.wallets);
          setWithdrawalFee(response.fees);
          setWithdrawAmount('');
          const newWallet = response?.wallets.length === 1 ? response?.wallets[0].value : '';
          setSelectedWallet(newWallet);
        }
      })
      .catch(err => {
        if (isMounted) dispatch(showAlert('danger', err?.message));
      })
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [selectedCurrency, dispatch]);

  const formatNumber = (val) => {
    if (!val) return '';
    const [intPart, decimalPart] = val.split('.');
    const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return decimalPart !== undefined
      ? `${formattedInt}.${decimalPart.slice(0, 2)}`
      : formattedInt;
  };

  const stripFormatting = (val) => val.replace(/,/g, '');

  const handleChange = (e) => {
    const input = e.target;
    let raw = stripFormatting(input.value);

    // Only allow digits and at most one decimal point
    if (!/^\d*\.?\d*$/.test(raw)) return;

    // Restrict to max 2 decimals
    if (raw.includes('.')) {
      const parts = raw.split('.');
      if (parts[1].length > 2) {
        raw = parts[0] + '.' + parts[1].slice(0, 2);
      }
    }

    if (parseFloat(raw) > balance) return;
    setWithdrawAmount(raw);

    const formatted = formatNumber(raw);

    const prevPos = input.selectionStart;
    const prevLength = input.value.length;

    requestAnimationFrame(() => {
      input.value = formatted;
      const newLength = formatted.length;
      const posDiff = newLength - prevLength;
      input.setSelectionRange(prevPos + posDiff, prevPos + posDiff);
    });
  };

  const handleKeyDown = (e) => {
    const allowedKeys = [
      'Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete', 'Home', 'End',
      '.', // allow decimal point
    ];

    // Allow Ctrl/Command + keys (copy/paste/select all)
    if (e.ctrlKey || e.metaKey) return;

    if (
      !allowedKeys.includes(e.key) &&
      !/^\d$/.test(e.key) // digit keys only
    ) {
      e.preventDefault();
    }
  };

  const submitWithdrawalRequest = () => {
    setIsLoading(true);
    const data = {
      amount: withdrawAmount,
      clientId: merchantId,
      currency: selectedCurrency,
      date: moment().format('DD.MM.YYYY'),
      fees: withdrawalFee,
      walletId: selectedWallet
    };
    crudActions.post(`v1/wallets/withdrawals`, data)
      .then(() => {
        dispatch(showAlert('success', 'Requested withdrawal is waiting for approval'));
        setWithdrawAmount('');
        setSelectedWallet(wallets?.length === 1 ? wallets[0].value : '');
        setShouldRefreshList(crypto.randomUUID());
      })
      .catch(err => {
        dispatch(showAlert('danger', err?.message));
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const isWithdrawEntered = withdrawAmount > 0;

  return (
    <div className='col-12 module finances topUp withdrawal'>
      <div className='component shadow mb-4 position-relative'>
        <ModuleHeader
          id='article'
          title='Request Withdrawal'
          actions={
            <ScrollList
              items={ currencies }
              selectedItem={ selectedCurrency }
              onClick={ (value) => setSelectedCurrency(value) }
            />
          }
        />

        {isLoading && <Spinner /> }

        <div className='row d-flex align-items-stretch info'>
          <div className='col-sm-12 col-md-6 col-lg-4 item-wrap'>
            <div className='item item1'>
              <div className='header'>
                <Icon name='finance' />
                Balance
              </div>
              <div className='body'>
                <div>
                  <span className='currency'>{ selectedCurrency }</span>
                  <span className={ `number ${isWithdrawEntered ? 'gold' : ''}` }>
                    { formatBalance(balance - Number(withdrawAmount)) }
                  </span>
                </div>
                {isWithdrawEntered && (
                  <div>
                    <span className='chevron gold'>Future Balance</span>
                  </div>
                )}
              </div>
              <div className='arrow'>
                <Icon name='keyboard_double_arrow_right' style={ { marginRight: 0 } } color='#8a8a8a' />
              </div>
            </div>
          </div>
          <div className='col-sm-12 col-md-6 col-lg-4 item-wrap'>
            <div className='item item2'>
              <div className='header'>
                <Icon name='compare_arrows' />
                Amount to Withdraw
              </div>
              <div className='body d-flex justify-content-between align-items-end'>
                <div className='amount-wrap'>
                  <div>Enter amount</div>
                  <div className='d-flex align-items-center'>
                    <span className='currency'>{ selectedCurrency }</span>
                    <input
                      id='withdraw'
                      ref={ inputRef }
                      className="amount-input"
                      onKeyDown={ handleKeyDown }
                      onChange={ handleChange }
                      value={ formatNumber(withdrawAmount) }
                      placeholder="0.00"
                    />
                  </div>
                </div>
                <div className='additional'>
                  Withdrawal fee: <span>{withdrawalFee}%</span>
                </div>
              </div>
              <div className='arrow'>
                <Icon name='keyboard_double_arrow_right' style={ { marginRight: 0 } } color='#8a8a8a' />
              </div>
            </div>
          </div>
          <div className='col-sm-12 col-md-12 col-lg-4 mt-md-3 mt-lg-0 item-wrap'>
            <div className='item item3'>
              <div className='header'>
                <Icon name='account_balance_wallet' />
                Select Wallet
              </div>
              <div className='body d-flex justify-content-between align-items-center flex-row'>
                <div className='select-wrapper'>
                  <Select
                    id="wallet"
                    name="wallet"
                    label="Wallet"
                    value={ selectedWallet }
                    required={ true }
                    clearable={ false }
                    disabled={ isLoading }
                    onChange={ (e) => setSelectedWallet(e.value) }
                    options={ wallets }
                  />
                </div>
                <Button
                  label='Request'
                  disabled={ !balance || !withdrawAmount || !selectedWallet }
                  onClick={ () => submitWithdrawalRequest() }
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <LatestWithdrawals
        selectedCurrency={ selectedCurrency }
        isOtc={ false }
        shouldRefresh={ shouldRefreshList }
      />

    </div>
  );
};

export default RequestWithdrawal;